import lodash from 'lodash';
import { composeReducers } from 'common/util/composeReducers';
import { ACTIONS, DEFAULT_LIMIT, INITIAL_OFFSET } from 'part11/actions/users';


// TEMPORARY
function clonePath(objectToClone, path) {
    var pathRemaining = lodash.toPath(path);
    var cumulativePath = [];

    const cloneObject = lodash.clone(objectToClone);

    while (pathRemaining.length > 0) {
        const nextPath = pathRemaining.shift();
        cumulativePath.push(nextPath);
        let nextValue = lodash.clone(lodash.get(cloneObject, cumulativePath, {}));
        lodash.set(cloneObject, cumulativePath, nextValue);
    }

    return cloneObject;
}

export function loadUsers(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOAD_USERS) {
        return state;
    }

    const result = { ...state };

    const reset = lodash.get(action.payload, 'reset', false);
    const currentAdminUsers = lodash.get(result, 'adminUsers', []);
    const currentUsers = reset ? {} : lodash.get(result, 'users', {});
    const currentOffset = parseInt(lodash.get(currentAdminUsers, 'offset', INITIAL_OFFSET));
    const currentLimit = parseInt(lodash.get(currentAdminUsers, 'limit', DEFAULT_LIMIT));

    const newUsers = lodash.extend({}, currentUsers, lodash.keyBy(action.payload.users, 'id'));
    // It is possible for user data to be out of sync between keymaster and chili. (PT3-84)
    // In this case the count of total users for a query may be less than the total retrieved.
    // As a safety, stop pagination if we are attempting to retrieve more users (offset) than are in an institution (total users)
    const totalUsers = lodash.get(result, 'adminUsers.totalUsers', 0);
    const offsetLessThanTotal = totalUsers ? currentOffset < totalUsers : true;
    const hasMoreUsers = (lodash.keys(newUsers).length < action.payload.userCount) && offsetLessThanTotal;

    let offset = currentOffset + currentLimit;
    if (reset) {
        offset = currentLimit;
    }

    const adminUsers = {
        ...result.adminUsers,
        hasMoreUsers,
        offset,
        isLoading: false,
    };

    lodash.set(result, 'users', newUsers);
    lodash.set(result, 'adminUsers', adminUsers);
    return result;
}

export function loadUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOAD_USER_BINDERS) {
        return state;
    }

    const result = { ...state };
    const { data } = action.payload;
    const userBinders = {
        ...result.userBinders,
        existingData: data,
        isLoading: false
    };
    lodash.set(result, 'userBinders', userBinders);
    return result;
}

export function loadUserStudies(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOAD_USER_STUDIES) {
        return state;
    }
    const result = { ...state };
    const { data } = action.payload;
    const userStudies = {
        ...result.userStudies,
        existingData: data,
        isLoading: false
    };
    lodash.set(result, 'userStudies', userStudies);
    return result;
}

export function totalUsers(state, action) {
    if (!action || action.type !== ACTIONS.PART11_TOTAL_USERS) {
        return state;
    }

    const result = { ...state };
    lodash.set(result, 'adminUsers.totalUsers', action.payload.count);
    return result;
}

export function clearUsersScene(state, action) {
    if (!action || action.type !== ACTIONS.PART11_CLEAR_USERS_SCENE) {
        return state;
    }

    const result = { ...state };

    lodash.unset(result, 'users');
    lodash.unset(result, 'adminUsers');

    return result;
}

export function resetUserFormFlags(state, action) {
    if (!action || action.type !== ACTIONS.PART11_RESET_USER_FORM_FLAGS) {
        return state;
    }

    const result = {
        ...state
    };

    lodash.set(result, 'createUser.isLoading', action.payload.isLoading);
    lodash.set(result, 'createUser.shouldRedirect', action.payload.shouldRedirect);
    lodash.set(result, 'createUser.error', action.payload.error);

    return result;
}

export function removeUserFromBinder(state, action) {
    if (!action || action.type !== ACTIONS.PART11_REMOVE_USER_FROM_BINDER) {
        return state;
    }

    const { binderId } = action.payload;
    const allBinders = lodash.get(state, 'allUserBinders.existingData', []);
    const updatedBinders = allBinders.filter(binder => binder.id !== binderId);

    return {
        ...state,
        allUserBinders: {
            ...state.allUserBinders,
            existingData: updatedBinders,
        }
    };
}

export function createUserRequest(state, action) {
    if (!action || action.type !== ACTIONS.PART11_CREATE_USER_REQUEST) {
        return state;
    }

    const result = {
        ...state
    };

    lodash.set(result, 'createUser.isLoading', true);
    lodash.set(result, 'createUser.shouldRedirect', false);

    return result;
}

export function createUserSuccess(state, action) {
    if (!action || action.type !== ACTIONS.PART11_CREATE_USER_SUCCESS) {
        return state;
    }

    if (!action.payload || !action.payload.user) {
        return state;
    }

    const result = {
        ...state
    };

    const { user } = action.payload;

    const usersPath = 'users';

    const currentUsers = lodash.get(result, usersPath, {});
    currentUsers[user.id] = user;

    lodash.set(result, usersPath, currentUsers);
    lodash.set(result, 'createUser.shouldRedirect', true);
    lodash.set(result, 'createUser.isLoading', false);

    return result;
}

export function createUserFailure(state, action) {
    if (!action || action.type !== ACTIONS.PART11_CREATE_USER_FAILURE) {
        return state;
    }

    if (!action.payload || !action.payload.error) {
        return state;
    }

    const result = {
        ...state
    };

    lodash.set(result, 'createUser.shouldRedirect', false);
    lodash.set(result, 'createUser.isLoading', false);
    lodash.set(result, 'createUser.error', { isPresent: true, message: action.payload.error });

    return result;
}

export function setUserActive(state, action) {
    if (!action || action.type !== ACTIONS.PART11_SET_USER_ACTIVE) {
        return state;
    }

    const { userId, isActive } = action.payload;

    const path = `users.${userId}.isActive`;
    const result = clonePath(state, path);

    lodash.set(result, path, isActive);
    return result;
}

export function loadProcessStates(state, action) {
    if (!action || action.type !== ACTIONS.PART11_SET_PROCESS_STATES) {
        return state;
    }

    const result = {
        ...state
    };

    lodash.set(result, 'process.processStates', action.payload);
    const filter2Options = lodash.map(action.payload, ps => ({
        gqlName: ps.name,
        displayName: ps.displayName
    }));
    filter2Options.unshift({
        gqlName: null,
        displayName: 'All Registration Statuses'
    });
    lodash.set(result, 'app.search.filter2Options', filter2Options);

    return result;
}

export function loadLoginMethods(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOAD_LOGIN_METHODS) {
        return state;
    }

    const loginMethods = action.payload;

    const result = {
        ...state,
        loginMethods,
    };

    return result;
}

export function loadingUsers(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADING_USERS) {
        return state;
    }

    return {
        ...state,
        adminUsers: {
            ...state.adminUsers,
            isLoading: true,
        }
    };
}

export function loadingUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADING_USER_BINDERS) {
        return state;
    }

    return {
        ...state,
        userBinders: {
            ...state.userBinders,
            isLoading: true,
        },
    };
}

export function loadingUserStudies(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADING_USER_STUDIES) {
        return state;
    }

    return {
        ...state,
        userStudies: {
            ...state.userStudies,
            isLoading: true,
        },
    };
}

export function loadedUsers(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADED_USERS) {
        return state;
    }

    return {
        ...state,
        adminUsers: {
            ...state.adminUsers,
            isLoading: false,
        }
    };
}

export function loadedUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADED_USER_BINDERS) {
        return state;
    }

    return {
        ...state,
        userBinders: {
            ...state.userBinders,
            isLoading: false,
        },
    };
}

export function loadedUserStudies(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADED_USER_STUDIES) {
        return state;
    }

    return {
        ...state,
        userStudies: {
            ...state.userStudies,
            isLoading: false,
        },
    };
}

export const setUserActiveTasks = (state, action) => {
    if (!action || action.type !== ACTIONS.PART11_TOTAL_ACTIVE_TASKS) {
        return state;
    }

    const { userId, totalActiveTasks } = action.payload;

    return {
        ...state,
        deactivateUser: {
            userId,
            totalActiveTasks,
        }
    };
};

export function resetUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_RESET_USER_BINDERS) {
        return state;
    }

    return {
        ...state,
        userBinders: undefined
    };
}

export function resetUserStudies(state, action) {
    if (!action || action.type !== ACTIONS.PART11_RESET_USER_STUDIES) {
        return state;
    }

    return {
        ...state,
        userStudies: undefined
    };
}

export function loadAllUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOAD_ALL_USER_BINDERS) {
        return state;
    }

    const { data } = action.payload;
    return {
        ...state,
        allUserBinders: {
            ...state.allUserBinders,
            existingData: data,
            isLoading: false,
        }
    };
}

export function loadingAllUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADING_ALL_USER_BINDERS) {
        return state;
    }

    return {
        ...state,
        allUserBinders: {
            ...state.allUserBinders,
            isLoading: true,
        }
    };
}

export function loadedAllUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_LOADED_ALL_USER_BINDERS) {
        return state;
    }

    return {
        ...state,
        allUserBinders: {
            ...state.allUserBinders,
            isLoading: false,
        }
    };
}

export function resetAllUserBinders(state, action) {
    if (!action || action.type !== ACTIONS.PART11_RESET_ALL_USER_BINDERS) {
        return state;
    }

    return {
        ...state,
        allUserBinders: undefined
    };
}

export function removeUserFromStudyBinder(state, action) {
    if (!action || action.type !== ACTIONS.PART11_REMOVE_USER_FROM_STUDY_BINDER) {
        return state;
    }

    const { binderId } = action.payload;
    const allBinders = lodash.get(state, 'allUserBinders.existingData', []);
    const updatedBinders = allBinders.filter(binder => binder.id !== binderId);

    return {
        ...state,
        allUserBinders: {
            ...state.allUserBinders,
            existingData: updatedBinders,
        }
    };
}



export default composeReducers(
    loadUsers,
    resetUserFormFlags,
    clearUsersScene,
    createUserRequest,
    createUserSuccess,
    createUserFailure,
    setUserActive,
    loadProcessStates,
    loadLoginMethods,
    loadingUsers,
    loadedUsers,
    totalUsers,
    setUserActiveTasks,
    loadUserStudies,
    loadUserBinders,
    loadingUserBinders,
    loadingUserStudies,
    loadedUserBinders,
    loadedUserStudies,
    resetUserBinders,
    resetUserStudies,
    removeUserFromBinder,
    loadAllUserBinders,
    loadingAllUserBinders,
    loadedAllUserBinders,
    resetAllUserBinders,
    removeUserFromStudyBinder,
);
